import { useCallback, useMemo, useState } from "react";

import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import isEmpty from "lodash/isEmpty";

import { useGroupedDeviceTableRows } from "../hooks/useGroupedDeviceTableRows";
import { AggregateStatus, ISKUGroup } from "../types/groupedDevices";
import DevicesTableNoRowsOverlay from "./DevicesTableNoRowsOverlay";
import GroupedDevicesTableBodyRow from "./table/GroupedDevicesTableBodyRow";
import GroupedDevicesTableHeadRow, {
  SortableColumn,
  SortDirection,
} from "./table/GroupedDevicesTableHeadRow";

/** Priority order for aggregate status sorting (lower = better) */
const STATUS_PRIORITY: Record<AggregateStatus, number> = {
  online: 0,
  degraded: 1,
  offline: 2,
};

/**
 * Comparator for sorting groups
 * @param {ISKUGroup} a - First group
 * @param {ISKUGroup} b - Second group
 * @param {SortableColumn} orderBy - Column to sort by
 * @return {number} Comparison result
 */
const compareGroups = (
  a: ISKUGroup,
  b: ISKUGroup,
  orderBy: SortableColumn
): number => {
  // Special handling for aggregateStatus - use priority order
  if (orderBy === "aggregateStatus") {
    const aPriority = STATUS_PRIORITY[a.aggregateStatus] ?? 3;
    const bPriority = STATUS_PRIORITY[b.aggregateStatus] ?? 3;

    return aPriority - bPriority;
  }

  const aValue = a[orderBy];
  const bValue = b[orderBy];

  // Handle null/undefined values - push them to the end
  if (aValue == null && bValue == null) return 0;

  if (aValue == null) return 1;

  if (bValue == null) return -1;

  // String comparison
  if (typeof aValue === "string" && typeof bValue === "string") {
    return aValue.localeCompare(bValue);
  }

  return 0;
};

/**
 * Displays devices grouped by SKU Serial Number using master-detail pattern.
 *
 * - Master row shows: SKU, SKU SN, Configuration, aggregate Status
 * - Detail row shows: nested table with individual device components
 * - Devices without serialNumber appear as individual groups
 * @return {JSX.Element} The grouped devices table
 */
const GroupedDevicesTable = (): JSX.Element => {
  const { groups, loading } = useGroupedDeviceTableRows();
  const [orderBy, setOrderBy] = useState<SortableColumn>("skuNumber");
  const [order, setOrder] = useState<SortDirection>("asc");

  const handleSort = useCallback(
    (column: SortableColumn) => {
      if (column === orderBy) {
        // Toggle direction if same column
        setOrder(prevOrder => (prevOrder === "asc" ? "desc" : "asc"));
      } else {
        // New column - set it and reset to asc
        setOrderBy(column);

        setOrder("asc");
      }
    },
    [orderBy]
  );

  const sortedGroups = useMemo(() => {
    if (!groups.length) return groups;

    return [...groups].sort((a, b) => {
      const comparison = compareGroups(a, b, orderBy);

      return order === "asc" ? comparison : -comparison;
    });
  }, [groups, orderBy, order]);

  if (!loading && isEmpty(groups)) {
    return <DevicesTableNoRowsOverlay />;
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: "12px",
        border: "none",
        overflowX: "auto",
      }}
    >
      <Table sx={{ tableLayout: "fixed", minWidth: 1145 }}>
        <GroupedDevicesTableHeadRow
          orderBy={orderBy}
          order={order}
          onSort={handleSort}
        />
        <TableBody>
          {!loading &&
            sortedGroups.map(group => (
              <GroupedDevicesTableBodyRow key={group.groupId} group={group} />
            ))}

          {loading && (
            <TableRow>
              <TableCell colSpan={7}>
                <Box sx={{ py: 2 }}>
                  {[...Array(5)].map((_, index) => (
                    <Skeleton
                      key={index}
                      variant="rectangular"
                      sx={{ my: 2, height: 40 }}
                    />
                  ))}
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default GroupedDevicesTable;
